import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Alert, Button, Typography, Spin } from "antd"
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import { motion, AnimatePresence } from "framer-motion"
import Confetti from "react-confetti"
import { useWindowSize } from "react-use"
import supabase from "../../config/supabaseConfig"
import { establishSessionFromUrl } from "../../utils/authHelpers"
import { ensureFreeSubscription } from "../../utils/subscriptionHelpers"

const { Title, Text, Paragraph } = Typography

const AuthCallback = () => {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading")
  const { width, height } = useWindowSize()

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setStatus("loading")
        console.log("Starting auth callback process...")

        const {
          session,
          error: sessionSetupError,
          authType,
        } = await establishSessionFromUrl()

        if (sessionSetupError) {
          console.error("Session setup error:", sessionSetupError)
          throw sessionSetupError
        }

        if (authType === "recovery" && session) {
          navigate("/auth/reset-password", { replace: true })
          return
        }

        if (!session?.user) {
          console.error("No session found after callback")
          throw new Error("No session found")
        }

        const user = session.user
        console.log("Step 1: User authenticated:", user.email)
        console.log("Step 2: User metadata:", user.user_metadata)
        console.log("Step 2: App metadata:", user.app_metadata)

        // Check if email is confirmed
        if (!user.email_confirmed_at) {
          throw new Error("Email not confirmed")
        }

        const userType = user.user_metadata?.user_type
        const isOAuthUser = user.app_metadata?.provider !== "email"

        console.log("Step 3: userType:", userType, "| isOAuthUser:", isOAuthUser)

        // ── ADMIN ──────────────────────────────────────────────────────────
        if (userType === "admin") {
          const { error: upsertError } = await supabase
            .from("admins")
            .upsert(
              {
                id: user.id,
                name: user.user_metadata?.name || user.email?.split("@")[0] || "Admin",
                email: user.email,
                is_verified: true,
                is_online: true,
                approved: false,
                updated_at: new Date().toISOString(),
              },
              { onConflict: "id" }
            )

          if (upsertError) {
            console.error("Admin record creation error:", upsertError)
            throw new Error("Admin record creation failed")
          }

          // Admins are a separate identity from profiles (admins are never
          // expected to shop/sell as a regular user). But ads.user_id and
          // ads.posted_by are NOT NULL foreign keys into profiles, so an
          // admin who wants to post an ad "as themselves" from the Admin
          // Dashboard needs a matching profiles row to exist -- otherwise
          // that insert fails with a foreign key violation (ads_user_id_fkey).
          // is_admin is left false here: it's just a cosmetic flag read by
          // the regular-user nav (MainNav/useDashboard), unrelated to real
          // admin permissions (those are gated by the separate `admins`
          // table everywhere in RLS) -- and the self-insert RLS policy on
          // profiles forbids setting is_admin true on your own row anyway.
          const { error: adminProfileError } = await supabase
            .from("profiles")
            .upsert(
              {
                id: user.id,
                name: user.user_metadata?.name || user.email?.split("@")[0] || "Admin",
                email: user.email,
                phone: "",
                is_verified: true,
                is_online: false,
                is_admin: false,
                updated_at: new Date().toISOString(),
              },
              { onConflict: "id" }
            )

          if (adminProfileError) {
            // Non-fatal: admin login itself must not be blocked by this --
            // only admin-created ads depend on this row, and that path will
            // surface its own clear error if it's ever missing.
            console.error("Admin profile provisioning error:", adminProfileError)
          }

          setStatus("success")
          setTimeout(() => navigate("/login-admin", { replace: true }), 3000)
          return
        }

        // ── OAUTH USER (Google, etc.) ──────────────────────────────────────
        if (isOAuthUser) {
          console.log("Step 4: Handling OAuth user profile upsert")

          const { error: upsertError } = await supabase.from("profiles").upsert(
            {
              id: user.id,
              name:
                user.user_metadata?.full_name ||
                user.user_metadata?.name ||
                user.email?.split("@")[0] ||
                "User",
              email: user.email,
              phone: user.user_metadata?.phone || "",
              photo_url:
                user.user_metadata?.avatar_url ||
                user.user_metadata?.picture ||
                "",
              is_verified: true,
              is_online: true,
              is_admin: false,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "id" }
          )

          if (upsertError) {
            console.error("Step 4 ERROR: OAuth profile upsert failed:", upsertError)
            throw upsertError
          }

          console.log("Step 5: OAuth profile upsert successful, ensuring subscription")
          const subResult = await ensureFreeSubscription(user.id, user.email || "")
          if (!subResult.subscription) {
            console.error("Step 5: Subscription setup failed")
          }

          console.log("Step 6: Done — redirecting to home")
          setStatus("success")
          setTimeout(() => navigate("/", { replace: true }), 2000)
          return
        }

        // ── EMAIL/PASSWORD USER ────────────────────────────────────────────
        console.log("Step 4: Handling email user profile check")

        const { data: existingProfile, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle()

        console.log("Step 5: Profile check result:", { existingProfile, profileError })

        if (profileError && profileError.code !== "PGRST116") {
          console.error("Profile check error:", profileError)
          throw profileError
        }

        const userData = user.user_metadata || {}
        let profileCreated = false

        if (!existingProfile) {
          console.log("Step 6: Creating new profile for:", user.email)

          const { error: insertError } = await supabase.from("profiles").upsert(
            {
              id: user.id,
              name:
                userData.name ||
                userData.full_name ||
                user.email?.split("@")[0] ||
                "User",
              email: user.email,
              phone: userData.phone || "",
              photo_url: userData.photo_url || userData.avatar_url || "",
              is_verified: true,
              is_online: true,
              is_admin: false,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
            { onConflict: "id" }
          )

          if (insertError) {
            console.error("Step 6 ERROR: Profile insert failed:", insertError)
            throw insertError
          }

          profileCreated = true
          console.log("Step 6: Profile created successfully")
        } else {
          console.log("Step 6: Updating existing profile")

          const { error: updateError } = await supabase
            .from("profiles")
            .update({
              is_online: true,
              is_verified: true,
              updated_at: new Date().toISOString(),
            })
            .eq("id", user.id)

          if (updateError) {
            console.error("Step 6 ERROR: Profile update failed:", updateError)
            throw updateError
          }
        }

        // Only provision a free subscription for brand-new accounts — not on
        // every subsequent auth callback (which was creating duplicate rows).
        if (profileCreated) {
          console.log("Step 7: Ensuring free subscription for new user")
          try {
            await ensureFreeSubscription(user.id, user.email || "")
          } catch (subErr) {
            console.error("Step 7: Subscription error (non-fatal):", subErr)
          }
        }

        console.log("Step 8: Done — redirecting to login")
        setStatus("success")
        setTimeout(() => navigate("/auth/login", { replace: true }), 3000)

      } catch (err) {
        console.error("Authentication callback error:", err)
        setError(err instanceof Error ? err.message : "Authentication error")
        setStatus("error")
        setTimeout(() => navigate("/auth/login", { replace: true }), 5000)
      }
    }

    handleCallback()
  }, [navigate])

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-100 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="h-20 bg-gradient-to-r from-blue-600 to-purple-600"></div>
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto -mt-16 shadow-lg border-4 border-white">
              <LoadingOutlined className="text-blue-600 text-3xl" />
            </div>
            <Title level={3} className="mt-4 mb-2">Configuration en cours</Title>
            <Paragraph className="text-gray-500 mb-6">
              Veuillez patienter pendant que nous configurons votre compte...
            </Paragraph>
            <div className="flex justify-center">
              <Spin size="large" />
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-100 p-4">
      <AnimatePresence>
        {status === "success" && (
          <Confetti
            width={width}
            height={height}
            recycle={false}
            numberOfPieces={500}
            gravity={0.1}
          />
        )}

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div
            className={`h-20 ${
              status === "success"
                ? "bg-gradient-to-r from-emerald-500 to-green-500"
                : "bg-gradient-to-r from-red-500 to-orange-500"
            }`}
          ></div>
          <div className="p-8 text-center">
            <div
              className={`w-20 h-20 ${
                status === "success" ? "bg-green-100" : "bg-red-100"
              } rounded-full flex items-center justify-center mx-auto -mt-16 shadow-lg border-4 border-white`}
            >
              {status === "success" ? (
                <CheckCircleOutlined className="text-green-600 text-3xl" />
              ) : (
                <CloseCircleOutlined className="text-red-600 text-3xl" />
              )}
            </div>

            {status === "success" ? (
              <>
                <Title level={3} className="mt-4 mb-2">
                  Compte vérifié avec succès!
                </Title>
                <Paragraph className="text-gray-500 mb-6">
                  Votre compte a été activé. Vous allez être redirigé automatiquement.
                </Paragraph>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate("/")}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-12 font-medium"
                  >
                    Aller à l'accueil
                  </Button>
                </motion.div>
                <Text className="block text-gray-400 mt-4">
                  Redirection automatique dans quelques secondes...
                </Text>
              </>
            ) : (
              <>
                <Title level={3} className="mt-4 mb-2">
                  Problème de vérification
                </Title>
                <Alert
                  message="Erreur de vérification"
                  description={
                    error || "Une erreur est survenue lors de la vérification de votre compte."
                  }
                  type="error"
                  showIcon
                  className="mb-6 text-left"
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={() => navigate("/auth/login")}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-12 font-medium"
                >
                  Retour à la connexion
                </Button>
                <Text className="block text-gray-400 mt-4">
                  Redirection automatique dans quelques secondes...
                </Text>
              </>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default AuthCallback